import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import { AuthResponse } from '@core/models/auth.model';
import { UserDetailResponse } from '../models/response/user-detail-response.model';
import { LoginRequest } from '../models/request/login-request.model';
import { Employment } from '@core/models/common.model';
import { TableAccessRequest } from './qr-processing-service';

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/auth/login`, credentials);
  }

  loginWithGoogle(idToken: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/auth/google`, { idToken });
  }

  refreshToken(token: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/auth/refresh`, { refreshToken: token });
  }

  logout(refreshToken: string | null): Observable<void> {
    if (!refreshToken) return of(void 0);
    return this.http.post<void>(`${this.baseUrl}/auth/logout`, { refreshToken });
  }

  register(data: unknown): Observable<UserDetailResponse> {
    return this.http.post<UserDetailResponse>(`${this.baseUrl}/auth/register`, data);
  }

  verifyEmail(token: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/validate-email`, token, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  resendVerificationEmail(email: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/forward-verification`, { email });
  }

  requestPasswordReset(data: { email: string }): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/forgot-password`, data);
  }

  performPasswordReset(data: { recoveryToken: string; newPassword: string }): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/reset-password`, data);
  }

  scanQR(request: TableAccessRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/table-sessions/scan-qr`, request);
  }

  getAvailableRoles(): Observable<Employment[]> {
    return this.http.get<Employment[]>(`${this.baseUrl}/auth/switch-roles/availables`);
  }

  selectRole(employmentId: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/auth/switch-roles/select`, {
      employmentId,
    });
  }
}
